/**
 * SearchableCheckboxList - Filterable checkbox list for multi-select
 *
 * Features:
 * - Search input to filter options
 * - Checkbox list with scroll for long lists
 * - Selected items shown with visual highlight
 * - Supports large lists (assignees, firms, etc.)
 */

import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

interface FilterOption {
  label: string;
  value: string;
}

interface SearchableCheckboxListProps {
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

export const SearchableCheckboxList = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Search...',
}: SearchableCheckboxListProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const lowerSearch = searchTerm.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerSearch),
    );
  }, [options, searchTerm]);

  // Sort options: selected first, then alphabetically
  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) => {
      const aSelected = selectedValues.includes(a.value);
      const bSelected = selectedValues.includes(b.value);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [filteredOptions, selectedValues]);

  const handleToggle = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 dark:text-zinc-500 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'w-full h-8 pl-8 pr-3 text-xs rounded-md',
            'bg-white dark:bg-zinc-800',
            'border border-gray-300 dark:border-zinc-700',
            'text-zinc-900 dark:text-zinc-100',
            'placeholder:text-zinc-400 dark:placeholder:text-zinc-500',
            'focus:outline-none focus:border-zinc-400 dark:focus:border-zinc-500',
          )}
        />
      </div>

      {/* Checkbox List */}
      <div className="overflow-y-auto space-y-1 max-h-52">
        {sortedOptions.length === 0 ? (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 py-2">
            {searchTerm ? 'No matches found' : 'No options available'}
          </p>
        ) : (
          sortedOptions.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            const checkboxId = `checkbox-${option.value}`;
            return (
              <label
                key={option.value}
                htmlFor={checkboxId}
                className={cn(
                  'flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors',
                  isSelected
                    ? 'bg-zinc-100 dark:bg-zinc-800'
                    : 'hover:bg-zinc-50 dark:hover:bg-zinc-800',
                )}
              >
                <input
                  id={checkboxId}
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(option.value)}
                  className="h-3.5 w-3.5 rounded border-zinc-950/20 dark:border-white/20 text-zinc-700 dark:text-zinc-300 focus:ring-zinc-500 dark:bg-zinc-900 cursor-pointer"
                />
                <span
                  className={cn(
                    'text-xs truncate',
                    isSelected
                      ? 'text-zinc-950 dark:text-white font-medium'
                      : 'text-zinc-600 dark:text-zinc-400',
                  )}
                  title={option.label}
                >
                  {option.label}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};
